// Aesthetic: Utilitarian AP work queue - slate palette, tight spacing, tabular
// numbers, emerald reserved for approve. The job here is throughput: the
// reviewer should be able to clear a stack without reading carefully.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { RefreshCw, CheckCheck, Inbox, Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import BulkProgressDialog from './BulkProgressDialog';
import { toast } from 'react-toastify';
import { cn } from '../lib/utils';
import ReviewCard from './ReviewCard';

const prodBase = process.env.REACT_APP_API_BASE;
const reviewQueueRoute = process.env.REACT_APP_REVIEW_QUEUE || '/api/reviewqueue';
const submitReviewRoute = process.env.REACT_APP_SUBMIT_REVIEW || '/api/submitreview';
const invoiceContentRoute = process.env.REACT_APP_INVOICE_CONTENT || '/api/invoicecontent';
const attachmentRoute = process.env.REACT_APP_ATTACHMENT || '/api/attachment';

/**
 * Flattens documents into one card per proposed invoice.
 *
 * A single scanned batch can hold several invoices, and each is approved
 * separately, so the queue is a list of segments rather than a list of files.
 */
const contentUrl = (driveItemId) =>
  driveItemId ? `${prodBase}${invoiceContentRoute}?id=${encodeURIComponent(driveItemId)}` : null;

function toRows(documents) {
  const rows = [];
  for (const doc of documents) {
    const proposals = Array.isArray(doc.proposals) ? doc.proposals : [];
    if (proposals.length === 0) {
      rows.push({ key: `${doc.id}-0`, doc, proposal: null, segmentIndex: 0 });
      continue;
    }
    proposals.forEach((proposal, i) => {
      if (proposal?.filed) return; // already handled in an earlier pass
      // A packing slip scanned in the same pass as an invoice belongs in Search
      // Archive, not here - there is nothing on it to index, and a card asking
      // for a voucher number it cannot have would just block the queue.
      if (proposal?.documentType === 'packing_slip') return;
      rows.push({ key: `${doc.id}-${i}`, doc, proposal, segmentIndex: i });
    });
  }
  return rows;
}

export function ReviewQueue({ onFiled, useremail }) {
  const [documents, setDocuments] = useState([]);
  // What the server holds, as against what it sent. Equal in every normal case
  // - they are tracked separately so the queue can say which it is rather than
  // leaving the reviewer to wonder.
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [selected, setSelected] = useState(() => new Set());
  const [settings, setSettings] = useState({ autoFileEnabled: false, autoFileMinBand: 'high' });
  // rowKey -> {rotation, crop}. Lives here so bulk approve files exactly what
  // the reviewer set up on each card.
  const [transforms, setTransforms] = useState({});
  const [bulkJobs, setBulkJobs] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);

  const rows = useMemo(() => toRows(documents), [documents]);

  /**
   * "This is a packing slip, not an invoice."
   *
   * Reclassifying moves the segment to the attachment queue and looks its
   * purchase-order number up straight away, so by the time the reviewer gets to
   * the Search Archive tab it has already found its voucher.
   */
  const handleSendToArchive = useCallback(
    async (documentId, segmentIndex) => {
      try {
        const res = await fetch(`${prodBase}${attachmentRoute}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'reclassify', documentId, segmentIndex })
        });
        const data = await res.json();
        if (!res.ok || data.success === false) {
          throw new Error(data.details || data.error || `Failed (${res.status})`);
        }
        toast.success('Moved to Search Archive to be attached to its voucher');
        setDocuments((prev) => prev.filter((d) => d.id !== documentId));
        onFiled?.();
      } catch (err) {
        toast.error(`Could not move it: ${err.message}`);
      }
    },
    [onFiled]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // No limit, on purpose. This screen is the queue, so a page of it would
      // be indistinguishable from a shorter backlog. The server sends `total`
      // either way, which is what lets the summary below promise "all".
      const response = await fetch(`${prodBase}${reviewQueueRoute}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Request failed');

      const docs = Array.isArray(data.documents) ? data.documents : [];
      setDocuments(docs);
      // An older backend sends neither field; falling back to what arrived
      // means the summary reads "all of them" rather than "0 of them".
      setTotalDocuments(Number.isFinite(data.total) ? data.total : docs.length);
      setTruncated(Boolean(data.truncated));
      setSettings({
        autoFileEnabled: Boolean(data.autoFileEnabled),
        autoFileMinBand: data.autoFileMinBand || 'high'
      });
      setSelected(new Set());
    } catch (err) {
      console.error('Failed to load review queue:', err);
      toast('Could not load the review queue.', {
        className: 'error-toast',
        autoClose: 3000,
        closeOnClick: true
      });
      setDocuments([]);
      setTotalDocuments(0);
      setTruncated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const markBusy = (id, busy) =>
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });

  const submit = useCallback(
    async ({ documentId, action, segmentIndex, fields, transform }) => {
      const response = await fetch(`${prodBase}${submitReviewRoute}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId,
          action,
          segmentIndex,
          fields,
          transform,
          reviewer: useremail || 'unknown'
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || data.details || `HTTP ${response.status}`);
      }
      return data;
    },
    [useremail]
  );

  const handleApprove = useCallback(
    async (documentId, segmentIndex, fields, transform) => {
      markBusy(documentId, true);
      try {
        const result = await submit({ documentId, action: 'approve', segmentIndex, fields, transform });
        toast(
          <div>
            <div className="font-semibold">Filed</div>
            <div className="mt-0.5 font-mono text-xs">{result.filedAs}</div>
          </div>,
          { className: 'success-toast', autoClose: 2500, closeOnClick: true }
        );
        await load();
        onFiled?.();
      } catch (err) {
        toast(`Could not file: ${err.message}`, {
          className: 'error-toast',
          autoClose: 4000,
          closeOnClick: true
        });
      } finally {
        markBusy(documentId, false);
      }
    },
    [submit, load, onFiled]
  );

  const handleReject = useCallback(
    async (documentId) => {
      markBusy(documentId, true);
      try {
        await submit({ documentId, action: 'reject' });
        toast('Marked as not an invoice.', {
          className: 'success-toast',
          autoClose: 2000,
          closeOnClick: true
        });
        await load();
      } catch (err) {
        toast(`Could not reject: ${err.message}`, {
          className: 'error-toast',
          autoClose: 4000,
          closeOnClick: true
        });
      } finally {
        markBusy(documentId, false);
      }
    },
    [submit, load]
  );

  /**
   * Bulk approve opens a progress dialog rather than firing and hoping.
   *
   * Filing writes to OneDrive, and on a run of fifty some of them will fail -
   * a lock, a name collision, an expired token. A summary toast saying "47
   * filed" leaves the operator with no idea which three to chase, so the dialog
   * itemises every file and keeps the failures on screen afterwards.
   */
  const startBulkApprove = useCallback(() => {
    const targets = rows.filter((row) => selected.has(row.key));
    if (targets.length === 0) return;

    setBulkJobs(
      targets.map((row) => ({
        key: row.key,
        label: row.doc.fileName || row.doc.id,
        sublabel: row.proposal?.proposedFileName || 'No filename proposed',
        payload: {
          documentId: row.doc.id,
          segmentIndex: row.segmentIndex,
          fields: row.proposal?.fields,
          transform: transforms[row.key] || null
        }
      }))
    );
    setBulkOpen(true);
  }, [rows, selected, transforms]);

  const runBulkJob = useCallback(
    async (job) => {
      const result = await submit({
        documentId: job.payload.documentId,
        action: 'approve',
        segmentIndex: job.payload.segmentIndex,
        fields: job.payload.fields,
        transform: job.payload.transform
      });
      return { detail: result.filedAs };
    },
    [submit]
  );

  const onBulkFinished = useCallback(
    async (summary) => {
      await load();
      onFiled?.();
      if (summary.failed > 0) {
        toast(
          `${summary.done} filed, ${summary.failed} could not be written to OneDrive.`,
          { className: 'error-toast', autoClose: 6000, closeOnClick: true }
        );
      }
    },
    [load, onFiled]
  );


  const toggleSelect = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // "Select all corroborated" is the fast path: the band where the page and TIMS
  // already agree, which is what a reviewer would wave through anyway.
  const corroboratedKeys = useMemo(
    () => rows.filter((r) => r.proposal?.band === 'high').map((r) => r.key),
    [rows]
  );

  const selectAllCorroborated = () =>
    setSelected((prev) =>
      corroboratedKeys.every((k) => prev.has(k)) ? new Set() : new Set(corroboratedKeys)
    );

  const allSelected = rows.length > 0 && rows.every((row) => selected.has(row.key));

  const toggleSelectAll = () =>
    setSelected(allSelected ? new Set() : new Set(rows.map((row) => row.key)));

  const counts = useMemo(() => {
    const byBand = { high: 0, medium: 0, low: 0, none: 0 };
    for (const row of rows) byBand[row.proposal?.band || 'none']++;
    return byBand;
  }, [rows]);

  // Files that actually put a card on screen, which is not every file returned:
  // a batch whose only segments are packing slips or already-filed invoices
  // contributes no rows. Counting the rows' own documents keeps both halves of
  // the summary describing the same thing - what is rendered.
  const fileCount = useMemo(() => new Set(rows.map((row) => row.doc.id)).size, [rows]);

  const plural = (n, noun) => `${n} ${noun}${n === 1 ? '' : 's'}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Review Queue
          </h2>
          <p className="mt-0.5 text-sm tabular-nums text-slate-500 dark:text-slate-400">
            {rows.length === 0
              ? 'Nothing waiting.'
              : truncated
              ? // The server was asked for a page rather than the queue, so what
                // is on screen is the front of a longer backlog. Say the two
                // numbers out loud: a count that quietly means "some of them"
                // is exactly what kept two documents invisible.
                `Showing ${plural(rows.length, 'invoice')} from the oldest ` +
                `${fileCount} of ${plural(totalDocuments, 'scanned file')}.`
              : // rows are invoices, documents are the scanned files they came
                // from, and one file can hold a stack of them - so the line
                // names which number is which rather than saying "documents"
                // and leaving the reviewer to guess.
                `Showing ${plural(rows.length, 'invoice')} from ` +
                `${plural(fileCount, 'scanned file')} - the whole queue, not a page of it.`}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {truncated && (
            // Only ever rendered when the response really was cut short. Grey
            // body text is the wrong weight for "there is work you cannot see".
            <Badge
              variant="outline"
              className="border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
              title={`${totalDocuments - documents.length} more scanned file(s) are waiting but were not returned.`}
            >
              <AlertTriangle className="mr-1 h-3 w-3" />
              <span className="tabular-nums">
                {totalDocuments - documents.length} not shown
              </span>
            </Badge>
          )}

          {settings.autoFileEnabled ? (
            <Badge
              variant="outline"
              className="border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
            >
              <ShieldCheck className="mr-1 h-3 w-3" />
              Auto-filing {settings.autoFileMinBand}+
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-400"
            >
              Every document reviewed
            </Badge>
          )}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            aria-label="Refresh review queue"
          >
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 dark:border-slate-800 dark:bg-slate-900">
          <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-700 dark:text-slate-300">
            <Checkbox
              checked={allSelected}
              onCheckedChange={toggleSelectAll}
              aria-label={allSelected ? 'Clear selection' : 'Select every document in the queue'}
            />
            <span className="tabular-nums">
              {selected.size > 0 ? `${selected.size} selected` : 'Select all'}
            </span>
          </label>

          <span className="h-4 w-px bg-slate-300 dark:bg-slate-700" />

          <div className="flex flex-wrap items-center gap-3 text-xs tabular-nums text-slate-600 dark:text-slate-400">
            <span>
              <strong className="text-emerald-700 dark:text-emerald-400">{counts.high}</strong>{' '}
              corroborated
            </span>
            <span>
              <strong className="text-amber-700 dark:text-amber-400">{counts.medium}</strong> partial
            </span>
            <span>
              <strong className="text-orange-700 dark:text-orange-400">{counts.low}</strong> voucher
              only
            </span>
            <span>
              <strong className="text-slate-700 dark:text-slate-300">{counts.none}</strong>{' '}
              unidentified
            </span>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {corroboratedKeys.length > 0 && corroboratedKeys.length < rows.length && (
              <Button type="button" variant="outline" size="sm" onClick={selectAllCorroborated}>
                {corroboratedKeys.every((k) => selected.has(k))
                  ? 'Clear'
                  : `Select ${corroboratedKeys.length} corroborated`}
              </Button>
            )}
            <Button type="button" variant="outline" size="sm" onClick={toggleSelectAll}>
              {allSelected ? 'Clear selection' : `Select all ${rows.length}`}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={startBulkApprove}
              disabled={selected.size === 0}
              className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 dark:bg-emerald-700 dark:hover:bg-emerald-600"
            >
              <CheckCheck className="mr-1 h-4 w-4" />
              Approve {selected.size > 0 ? selected.size : ''}
            </Button>
          </div>
        </div>
      )}

      {loading && rows.length === 0 && (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500 dark:text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading queue...
        </div>
      )}

      {!loading && rows.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 py-16 dark:border-slate-700"
        >
          <Inbox className="h-8 w-8 text-slate-400 dark:text-slate-600" />
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Queue is clear</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Scanned invoices appear here once they have been read and matched against TIMS.
          </p>
        </motion.div>
      )}

      <div className="space-y-3">
        <AnimatePresence mode="popLayout">
          {rows.map((row) => (
            <ReviewCard
              key={row.key}
              document={row.doc}
              proposal={row.proposal}
              segmentIndex={row.segmentIndex}
              previewUrl={contentUrl(row.doc.driveItemId)}
              isSelected={selected.has(row.key)}
              onToggleSelect={() => toggleSelect(row.key)}
              transform={transforms[row.key]}
              onTransformChange={(next) =>
                // Functional updates, because rotating turns the crops with the
                // page: rotation and every crop have to land in one write or the
                // later call overwrites the earlier with what it closed over.
                setTransforms((prev) => ({
                  ...prev,
                  [row.key]: typeof next === 'function' ? next(prev[row.key]) : next
                }))
              }
              onApprove={handleApprove}
              onReject={handleReject}
              onSendToArchive={handleSendToArchive}
              onPreview={() => window.open(contentUrl(row.doc.driveItemId), '_blank', 'noopener')}
              busy={busyIds.has(row.doc.id)}
            />
          ))}
        </AnimatePresence>
      </div>

      <BulkProgressDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        title="Filing to OneDrive"
        description="Each approved document is written one at a time. Anything that fails stays in the queue."
        jobs={bulkJobs}
        runJob={runBulkJob}
        onFinished={onBulkFinished}
        verb="Filed"
      />
    </div>
  );
}

export default ReviewQueue;
